import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const CATEGORIES = [
  { value: 'ui', label: 'UI Changes Needed', icon: '🎨' },
  { value: 'flow', label: 'Flow Changes Needed', icon: '🔄' },
  { value: 'feature', label: 'Feature Request', icon: '💡' },
  { value: 'bug', label: 'Bug Report', icon: '🐛' },
  { value: 'other', label: 'Other', icon: '💬' },
];

const FeedbackButton = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('File must be under 5MB'); return; }
    setScreenshot(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!category || !message.trim()) return;
    setSending(true);
    try {
      const cat = CATEGORIES.find(c => c.value === category);
      const subject = `[Feedback] ${cat?.label || category}`;
      const page = window.location.pathname;
      const fullMessage = `Page: ${page}\nCategory: ${cat?.label}\n\n${message.trim()}${screenshot ? '\n\n[Screenshot attached - see below]' : ''}`;

      await api.submitSupport({
        type: category === 'bug' ? 'bug' : 'feature',
        subject,
        message: fullMessage,
        userEmail: user?.email || '',
        userName: user?.displayName || '',
      });

      setSent(true);
      setTimeout(() => { setOpen(false); setSent(false); setCategory(''); setMessage(''); setScreenshot(null); setPreview(null); }, 2000);
    } catch {
      alert('Failed to send feedback. Please try again.');
    }
    setSending(false);
  };

  const reset = () => {
    setOpen(false); setCategory(''); setMessage('');
    setScreenshot(null); setPreview(null); setSent(false);
  };

  return (
    <>
      {/* Floating feedback button with label */}
      <button
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 80,
          height: 44, borderRadius: 22, padding: '0 18px 0 14px',
          background: 'linear-gradient(135deg, var(--accent-green), #7BAF6E)',
          border: 'none', cursor: 'pointer', color: '#FFF',
          boxShadow: '0 4px 16px rgba(91,140,81,0.4)',
          display: open ? 'none' : 'flex', alignItems: 'center', gap: 8,
          transition: 'transform 0.2s',
          fontFamily: "'Source Sans 3',sans-serif",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        <span style={{ fontSize: 18, lineHeight: 1 }}>📝</span>
        <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}>Feedback</span>
      </button>

      {/* Modal */}
      {open && (
        <>
          <div onClick={reset} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 90 }} />
          <div style={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 100,
            width: 'min(380px, calc(100vw - 32px))',
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 20, boxShadow: 'var(--shadow-lg)',
            animation: 'fadeUp 0.25s ease',
            overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Cormorant Garamond',serif" }}>Share Feedback</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>Help us improve LegacyRoots</div>
              </div>
              <button onClick={reset} style={{ background: 'none', border: 'none', fontSize: 18, color: 'var(--text-dim)', cursor: 'pointer', padding: 4 }}>✕</button>
            </div>

            {sent ? (
              <div style={{ padding: '40px 20px', textAlign: 'center' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>Thank you!</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>Your feedback has been submitted.</div>
              </div>
            ) : (
              <div style={{ padding: '16px 20px' }}>
                {/* Category */}
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>What type of feedback?</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  {CATEGORIES.map(c => (
                    <button
                      key={c.value}
                      onClick={() => setCategory(c.value)}
                      style={{
                        padding: '6px 12px', borderRadius: 20, cursor: 'pointer',
                        fontSize: 12, fontWeight: 600, fontFamily: "'Source Sans 3',sans-serif",
                        border: category === c.value ? '1.5px solid var(--accent-green)' : '1px solid var(--border)',
                        background: category === c.value ? 'var(--accent-green-light)' : 'var(--bg-secondary)',
                        color: category === c.value ? 'var(--accent-green)' : 'var(--text-secondary)',
                        transition: 'all 0.15s',
                      }}
                    >
                      {c.icon} {c.label}
                    </button>
                  ))}
                </div>

                {/* Message */}
                <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 6 }}>Your feedback</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Tell us what you think... What works well? What could be better?"
                  rows={4}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 12,
                    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                    color: 'var(--text-primary)', fontSize: 13, resize: 'none',
                    fontFamily: "'Source Sans 3',sans-serif", outline: 'none',
                    lineHeight: 1.5,
                  }}
                />

                {/* Screenshot */}
                <div style={{ marginTop: 10 }}>
                  <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
                  {preview ? (
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <img src={preview} alt="Screenshot" style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 10, border: '1px solid var(--border)' }} />
                      <button
                        onClick={() => { setScreenshot(null); setPreview(null); fileRef.current.value = ''; }}
                        style={{
                          position: 'absolute', top: -6, right: -6,
                          width: 20, height: 20, borderRadius: '50%',
                          background: 'var(--accent-rose)', border: 'none', color: '#FFF',
                          fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >✕</button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileRef.current?.click()}
                      style={{
                        padding: '8px 14px', borderRadius: 10,
                        border: '1px dashed var(--border)', background: 'transparent',
                        color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}
                    >
                      📎 Attach screenshot
                    </button>
                  )}
                </div>

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={!category || !message.trim() || sending}
                  style={{
                    width: '100%', padding: 12, borderRadius: 12, marginTop: 14,
                    background: (!category || !message.trim()) ? 'var(--border)' : 'linear-gradient(135deg, var(--accent-green), #7BAF6E)',
                    border: 'none', color: '#FFF', fontSize: 14, fontWeight: 700,
                    cursor: (!category || !message.trim()) ? 'not-allowed' : 'pointer',
                    opacity: sending ? 0.6 : 1,
                  }}
                >
                  {sending ? 'Sending...' : 'Submit Feedback'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default FeedbackButton;
